/* eslint-disable no-lonely-if */
import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { getAcmgVerdictClassifications } from "../../../../state/selectors/ACMGComponent.selectors";
import {
  getGnomadExomes,
  getGnomadExomesCoverage,
  getGnomadGenomes,
  getGnomadGenomesCoverage,
  getGnomadMito,
  getMitomap,
  isMitochondriac,
} from "../../../../state/selectors/Frequencies.selectors";
import { getPathogenicityColorByAllNames } from "../../../../common/helpers/Constant";
import { cropNumber } from "../../../../common/helpers/helpers";
import CardWrapper from "../../CardWrapper";
import Card from "../../Card";
import { GmomadExomesType } from "../../../componentTypes/GnomadExomesType";
import { GmomadGenomesType } from "../../../componentTypes/GnomadGenomesType";
import tailwindColors from "../../../../../../tailwind.colors";

const { arrayOf, number, shape, string } = PropTypes;

const mapRulesToPatho = [
  { name: "PM2_Strong", value: "Pathogenic" },
  { name: "PM2", value: "Likely Pathogenic" },
  { name: "PM2_Supporting", value: "Uncertain Significance" },
  { name: "BS1", value: "Likely Benign" },
  { name: "BS2", value: "Likely Benign" },
  { name: "BA1", value: "Benign" },
];

const FrequenciesCard = ({
  verdictClassifications,
  gnomadExomes,
  gnomadExomesCoverage,
  gnomadGenomes,
  gnomadGenomesCoverage,
  gnomadMito,
  isMito,
  card,
}) => {
  const isDisabled =
    !gnomadExomes &&
    !gnomadExomesCoverage &&
    !gnomadGenomes &&
    !gnomadGenomesCoverage &&
    !gnomadMito;

  const getBackGroundBasedOnACMGRules = () => {
    if (!verdictClassifications) return null;
    const pathoValues = mapRulesToPatho.map((item) => {
      return item.name;
    });
    const combinedArray = [...pathoValues, ...verdictClassifications];
    if (combinedArray && combinedArray.length > 0) {
      const classifications = combinedArray.filter(
        (e, i, a) => a.indexOf(e) !== i
      );
      if (classifications && classifications.length > 0) {
        const rule = mapRulesToPatho.find(
          (element) => element.name === classifications[0]
        );
        return rule.value;
      }
    }
    return "Uncertain Significance";
  };

  const calcFrequencyColor = (frequency) => {
    let freq;
    let { 700: color } = tailwindColors.blueGray;
    if (frequency) {
      freq = frequency * 100;
      if (freq >= 5) {
        color = getPathogenicityColorByAllNames("benign");
      } else if (freq >= 0.01) {
        color = getPathogenicityColorByAllNames("likely benign");
      } else if (freq < 0.01 && freq !== 0) {
        color = getPathogenicityColorByAllNames("pathogenic");
      } else if (freq === 0) {
        return color;
      } else {
        color = getPathogenicityColorByAllNames("VUS");
      }
    }
    return color;
  };

  const getFrequencyResultFor = (
    databaseName = "",
    frequency = null,
    coverage = null
  ) => {
    const verdict = {
      sourceFrequency: "",
      sourceFrequencyQuality: false,
      sourceCoverage: "",
      sourceName: databaseName,
    };
    if (frequency) {
      verdict.sourceFrequency = frequency[0]?.main_data;
      if (isMito) {
        if (frequency[0]?.filter === "PASS") {
          verdict.sourceFrequencyQuality = true;
        }
      } else {
        if (frequency[0]?.filter === "PASS" || frequency[0]?.filter === "AC0") {
          verdict.sourceFrequencyQuality = true;
        }
      }
    }
    if (coverage) {
      verdict.sourceCoverage = cropNumber(coverage[0]?.coverage_mean[0], 1);
    }
    return verdict;
  };

  const renderCardContent = (dataSources) => {
    return (
      <>
        {dataSources.map((dataSource, index) => {
          return (
            <div key={index}>
              <span>{dataSource.sourceName}: </span>
              <>
                <span>
                  {dataSource.sourceFrequency ? (
                    <>
                      {dataSource.sourceFrequencyQuality ? (
                        <span
                          className="tw-font-bold"
                          data-testid={`gnomad-value-${dataSource.sourceName}`}
                          style={
                            dataSource.sourceCoverage &&
                            dataSource.sourceCoverage > 20
                              ? {
                                  color: calcFrequencyColor(
                                    dataSource.sourceFrequency.slice(4)
                                  ),
                                }
                              : {}
                          }
                        >
                          {dataSource.sourceName === "mito" ? (
                            <span
                              style={{
                                color: calcFrequencyColor(
                                  dataSource.sourceFrequency.slice(4)
                                ),
                              }}
                            >
                              <span>ƒ = </span>
                              {dataSource.sourceFrequency.slice(4)}
                            </span>
                          ) : (
                            <>
                              <span>ƒ = </span>
                              {dataSource.sourceFrequency.slice(4)}
                            </>
                          )}
                        </span>
                      ) : (
                        <span
                          data-testid={`gnomad-value-${dataSource.sourceName}`}
                        >
                          fails QC
                        </span>
                      )}
                    </>
                  ) : (
                    <span
                      className="tw-font-bold"
                      style={
                        dataSource.sourceCoverage &&
                        dataSource.sourceCoverage > 20
                          ? {
                              color: getPathogenicityColorByAllNames("VUS"),
                            }
                          : {}
                      }
                      data-testid={`gnomad-value-${dataSource.sourceName}`}
                    >
                      not found
                    </span>
                  )}
                </span>
                <span>
                  {dataSource.sourceCoverage ? (
                    <span
                      data-testid={`gnomad-coverage-value-${dataSource.sourceName}`}
                    >
                      {` (cov: ${dataSource.sourceCoverage}`}
                      {dataSource.sourceCoverage > 20 ? ")" : <i>{` low)`}</i>}
                    </span>
                  ) : (
                    ""
                  )}
                </span>
              </>
            </div>
          );
        })}
      </>
    );
  };

  const renderBody = () => {
    const sources = [];
    if (isMito) {
      sources.push(getFrequencyResultFor("mito", gnomadMito));
    } else {
      sources.push(
        getFrequencyResultFor("exomes", gnomadExomes, gnomadExomesCoverage)
      );
      sources.push(
        getFrequencyResultFor("genomes", gnomadGenomes, gnomadGenomesCoverage)
      );
    }

    return renderCardContent(sources);
  };

  return (
    <CardWrapper
      {...card}
      isDisabled={isDisabled}
      color={getPathogenicityColorByAllNames(getBackGroundBasedOnACMGRules())}
    >
      {renderBody()}
    </CardWrapper>
  );
};

FrequenciesCard.propTypes = {
  card: PropTypes.instanceOf(Card).isRequired,
  gnomadExomes: GmomadExomesType,
  gnomadGenomes: GmomadGenomesType,
  verdictClassifications: arrayOf(string),
  gnomadGenomesCoverage: arrayOf(
    shape({
      coverage_20_frequency: arrayOf(number),
      coverage_mean: arrayOf(number),
      coverage_median: arrayOf(number),
      version: string,
    })
  ),
  gnomadExomesCoverage: arrayOf(
    shape({
      coverage_20_frequency: arrayOf(number),
      coverage_mean: arrayOf(number),
      coverage_median: arrayOf(number),
      version: string,
    })
  ),
  mitomap: arrayOf(
    shape({
      main_data: string,
    })
  ),
  gnomadMito: arrayOf(
    shape({
      main_data: string,
    })
  ),
  isMito: PropTypes.bool,
};

FrequenciesCard.defaultProps = {
  gnomadExomes: null,
  gnomadGenomes: null,
  gnomadGenomesCoverage: null,
  gnomadExomesCoverage: null,
  gnomadMito: null,
  mitomap: null,
  verdictClassifications: null,
  isMito: false,
};

const mapStateToProps = (state) => ({
  verdictClassifications: getAcmgVerdictClassifications(state),
  gnomadExomes: getGnomadExomes(state),
  gnomadExomesCoverage: getGnomadExomesCoverage(state),
  gnomadGenomes: getGnomadGenomes(state),
  gnomadGenomesCoverage: getGnomadGenomesCoverage(state),
  gnomadMito: getGnomadMito(state),
  mitomap: getMitomap(state),
  isMito: isMitochondriac(state),
});

export default connect(mapStateToProps)(FrequenciesCard);
